import {FC, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {
  createNuklieSession,
  getNuklieConversation,
  NuklieResource,
  NuklieStatus,
} from '../../../services/nuklieService'
import {Loading} from '../Loadings/loading'
import {NUKLIE_ICON} from './constants'
import styles from './nuklie.module.css'

declare global {
  interface Window {
    Nuklie?: {
      init: (config: {
        integration: string
        getSessionToken: () => Promise<string>
      }) => Promise<NuklieClient>
    }
  }
}

type NuklieInbox = {
  openConversation: (conversation: any) => void
}

type NuklieClient = {
  mount: (target: HTMLElement | string, options: {mode: 'inbox'}) => NuklieInbox
  on: (event: string, handler: (payload: any) => void) => void
  destroy: () => void
}

export type NuklieInboxRequest = {
  key: number
  resource?: NuklieResource
  id?: string
}

type Props = {
  status: NuklieStatus
  request: NuklieInboxRequest
  userEmail?: string
  onClose: () => void
}

let sdkLoading: Promise<void> | null = null

// Carrega o SDK uma vez por página; se falhar, a próxima abertura tenta de novo.
const loadNuklieSdk = (src: string) => {
  if (window.Nuklie) return Promise.resolve()
  if (!sdkLoading) {
    sdkLoading = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.src = src
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => {
        script.remove()
        sdkLoading = null
        reject(new Error('Não foi possível carregar o atendimento Nuklie.'))
      }
      document.head.appendChild(script)
    })
  }
  return sdkLoading
}

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error || error?.message || fallback

const modalsRoot = document.getElementById('root-modals') || document.body

export const NuklieInboxModal: FC<Props> = ({status, request, userEmail, onClose}) => {
  const container = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    const target = container.current
    if (!target) return

    // `init` é assíncrono: no StrictMode o efeito roda, limpa e roda de novo,
    // e sem esta bandeira o primeiro `init` monta um iframe que ninguém desmonta.
    let cancelled = false
    let client: NuklieClient | null = null

    setLoading(true)
    setError(null)
    setNotice(null)

    ;(async () => {
      try {
        const [conversation] = await Promise.all([
          request.resource && request.id
            ? getNuklieConversation(request.resource, request.id)
            : Promise.resolve(null),
          loadNuklieSdk(status.sdk_url),
        ])

        if (cancelled || !window.Nuklie) return

        const created = await window.Nuklie.init({
          integration: status.integration_public_identifier || '',
          getSessionToken: createNuklieSession,
        })

        if (cancelled) return created.destroy()
        client = created

        created.on('AUTH_ERROR', ({reason}: any = {}) => {
          setError(
            `Não foi possível entrar no atendimento${reason ? ` (${reason})` : ''}. ` +
              `Confira se o e-mail ${userEmail || 'do seu usuário'} está na lista de membros do projeto na Nuklie.`
          )
        })
        created.on('SESSION_EXPIRED', () => {
          setNotice('A sessão de atendimento expirou. Feche e abra novamente.')
        })
        created.on('CONVERSATION_STARTED', () => setNotice(null))

        const inbox = created.mount(target, {mode: 'inbox'})
        if (conversation) inbox.openConversation(conversation)
        setLoading(false)
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err, 'Não foi possível abrir o atendimento Nuklie.'))
          setLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
      client?.destroy()
    }
  }, [request, status.sdk_url, status.integration_public_identifier, userEmail])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(event) => event.stopPropagation()}>
        <button type='button' className={styles.closeButton} onClick={onClose}>
          <i className='bi bi-x-circle-fill'></i>
          Fechar
        </button>

        {loading && !error && (
          <div className={styles.state}>
            <Loading myClass='spinner-border' style={{width: '3rem', height: '3rem'}} />
          </div>
        )}

        {error && (
          <div className={styles.state}>
            <img src={NUKLIE_ICON} alt='' width={56} className='mb-4' />
            <span className='fs-5 fw-semibold text-gray-800 text-center mw-500px'>{error}</span>
          </div>
        )}

        {notice && !error && <div className={styles.notice}>{notice}</div>}

        <div ref={container} className={styles.inbox} style={{display: error ? 'none' : 'block'}} />
      </div>
    </div>,
    modalsRoot
  )
}
